import { parseChartData, reconcileIncrementalBars } from "./data";
import { CanvasRenderer } from "../renderers/canvasRenderer";
import { defaultTheme, type BridgeChartTheme } from "../themes/defaultTheme";
import { applyToolbarCommand, createToolbarState } from "./toolbar";
import { DrawingStore } from "../drawings/drawingStore";
import type {
  BridgeChartApi,
  BridgeChartDrawing,
  BridgeChartDrawingHandle,
  BridgeChartDrawingHit,
  BridgeChartDrawingKind,
  BridgeChartDrawingMigrationContext,
  BridgeChartDrawingPoint,
  BridgeChartDrawingState,
  BridgeChartDrawingAnchors,
  BridgeChartExportProvenance,
  BridgeChartEventHandler,
  BridgeChartInteractionOptions,
  BridgeChartInspection,
  BridgeChartOptions,
  BridgeChartPeriod,
  BridgeChartPresentationState,
  BridgeChartTimeframe,
  BridgeChartToolbarState,
  BridgeChartType,
  BridgeChartViewport
} from "../types/public";
import type { BridgeChartIncrementalUpdate, BridgeChartIncrementalUpdateResult, BridgeChartInput, NormalizedBridgeChartData } from "../types/data";
import type { BridgeChartStudyCode } from "../types/data";
import { exportVisibleData } from "../tools/visibleDataExport";
import { createDataPackage, validateExportProvenance } from "../tools/exportProvenance";
import { ChartAccessibility } from "../tools/chartAccessibility";

export class BridgeChartInstance implements BridgeChartApi {
  private readonly container: HTMLElement;
  private readonly canvas: HTMLCanvasElement;
  private readonly tooltip?: HTMLElement;
  private readonly status: HTMLElement;
  private readonly toolbar?: HTMLElement;
  private readonly renderer: CanvasRenderer;
  private readonly handlers = new Map<string, Set<BridgeChartEventHandler>>();
  private readonly disposers: Array<() => void> = [];
  private resizeObserver?: ResizeObserver;
  private theme: BridgeChartTheme;
  private drag: { x: number; start: number; end: number } | null = null;
  private readonly pointers = new Map<number, { x: number; y: number }>();
  private pointerDrag: { pointerId: number; x: number; start: number; end: number } | null = null;
  private pinch: { distance: number; centerIndex: number; start: number; end: number } | null = null;
  private currentData: NormalizedBridgeChartData;
  private presentation: BridgeChartPresentationState;
  private presentationExplicit: boolean;
  private toolbarStateValue: BridgeChartToolbarState;
  private readonly drawingStore: DrawingStore;
  private drawingGesture: DrawingGesture | null = null;
  private accessibility: ChartAccessibility | null = null;
  private ownsCanvasLabel = false;
  private keyboardIndex = 0;
  private keyboardPrice = 0.5;
  private keyboardAnchor: BridgeChartDrawingAnchors | null = null;
  private exportProvenance: BridgeChartExportProvenance | null = null;
  private drawingPriceBasis = "fixed-price";
  private inspectedIndex: number | null = null;
  private inspectionViewport = "";
  readonly overlays: unknown[] = [];

  constructor(container: HTMLElement, options: BridgeChartOptions = {}) {
    this.container = container;
    this.theme = { ...defaultTheme, ...options.theme };
    this.currentData = parseChartData({ ...(options.data || {}), timeframe: options.timeframe || options.data?.timeframe });
    const interaction = normalizeInteraction(options.interaction);
    this.presentationExplicit = options.presentation?.status !== undefined;
    this.presentation = normalizePresentation(options.presentation, this.currentData.bars.length === 0 ? "empty" : "ready");
    this.canvas = findOrCreateCanvas(container);
    this.tooltip = findOrCreateTooltip(container);
    this.status = findOrCreateStatus(container);
    this.toolbar = findToolbar(container);
    this.renderer = new CanvasRenderer(this.canvas, this.tooltip, this.currentData, this.theme, interaction, options.panes, options.studies);
    this.renderer.setMarkerVisibility(options.showMarkers ?? true);
    this.renderer.setDrawingPriceBasis(options.drawingPriceBasis ?? "fixed-price");
    this.drawingPriceBasis = options.drawingPriceBasis ?? "fixed-price";
    if (options.exportProvenance) this.setExportProvenance(options.exportProvenance);
    this.drawingStore = new DrawingStore(options.drawings, () => this.renderer.drawingTransform());
    this.renderer.setDrawings(this.drawingStore.state());
    this.toolbarStateValue = createToolbarState(this.currentData, this.renderer.studyStates());
    this.attach();
    this.attachToolbar();
    this.resize();
    this.applyPeriodViewport();
    this.keyboardIndex = Math.max(0, this.viewport.start);
    this.accessibility = ChartAccessibility.attach(container, {
      data: () => this.currentData, viewport: () => this.viewport, drawings: () => this.drawingState, cursor: () => this.keyboardIndex,
      command: key => this.keyboardCommand(key), goToSession: date => this.goToSession(date),
      showRecordedRange: (first, last) => this.showRecordedRange(first, last),
      maximumRangeBars: () => this.performanceState.budget.maxVisibleBars,
      canExport: () => this.exportProvenance !== null && this.currentData.bars.length > 0,
      exportPackage: () => this.exportDataPackage()
    }, this.presentation.locale);
    for (const [name, value] of [["tabindex", "0"], ["role", "group"], ["aria-label", this.presentation.locale === "fa" ? "نمودار تعاملی؛ کنترل با صفحه‌کلید" : "Interactive chart; keyboard controls"]]) {
      if (this.canvas.getAttribute?.(name) == null) {
        if (name === "aria-label") this.ownsCanvasLabel = true;
        this.canvas.setAttribute?.(name, value); this.disposers.push(() => this.canvas.removeAttribute?.(name));
      }
    }
    if (this.accessibility) {
      const previous = this.canvas.getAttribute("aria-describedby");
      this.canvas.setAttribute("aria-describedby", [previous, this.accessibility.help.id].filter(Boolean).join(" "));
      this.disposers.push(() => previous === null ? this.canvas.removeAttribute("aria-describedby") : this.canvas.setAttribute("aria-describedby", previous));
    }
    this.renderPresentation();
    this.renderToolbar();
    this.emit("ready", this);
  }

  get data() {
    return this.currentData;
  }

  get strategyMarkers() {
    return this.currentData.strategyMarkers;
  }

  get studies() {
    return this.renderer.studyStates();
  }

  get viewport(): BridgeChartViewport {
    return { start: this.renderer.viewStart, end: this.renderer.viewEnd };
  }

  get paneLayout() {
    return this.renderer.paneStates();
  }

  get presentationState(): BridgeChartPresentationState {
    return { ...this.presentation };
  }

  get toolbarState(): BridgeChartToolbarState {
    return {
      ...this.toolbarStateValue,
      enabledIndicators: [...this.toolbarStateValue.enabledIndicators],
      extensions: this.toolbarStateValue.extensions.map(extension => ({ ...extension }))
    };
  }

  get drawingState(): BridgeChartDrawingState {
    return this.drawingStore.state();
  }

  get performanceState() {
    return this.renderer.performanceState();
  }

  setData(chartData: BridgeChartInput): void {
    this.currentData = parseChartData(chartData);
    this.clearInspection("Data");
    this.exportProvenance = null; this.keyboardAnchor = null; this.drawingGesture = null;
    this.renderer.setData(this.currentData);
    this.syncToolbarState();
    this.applyPeriodViewport();
    if (!this.presentationExplicit) {
      this.presentation = normalizePresentation({ locale: this.presentation.locale }, this.currentData.bars.length === 0 ? "empty" : "ready");
      this.renderPresentation();
    }
    this.renderToolbar();
    this.keyboardIndex = Math.max(0, Math.min(this.keyboardIndex, this.currentData.bars.length - 1));
    this.accessibility?.update();
    this.emit("data", this.currentData);
    this.emit("toolbar", this.toolbarState);
  }

  updateData(partialData: Partial<BridgeChartInput>): void {
    const timeframe = partialData.metadata?.timeframe ?? partialData.timeframe;
    if (timeframe !== undefined) this.requireCurrentTimeframe(timeframe);
    this.setData({ ...this.currentData, ...partialData });
  }

  applyIncrementalUpdate(update: BridgeChartIncrementalUpdate): BridgeChartIncrementalUpdateResult {
    const reconciled = reconcileIncrementalBars(this.currentData.bars, update);
    if (reconciled.result.status === "Rejected") return reconciled.result;
    const bars = reconciled.bars;
    this.exportProvenance = null; this.keyboardAnchor = null; this.drawingGesture = null;
    const dateIndex: Record<string, number> = {};
    bars.forEach((bar, index) => { dateIndex[bar.date] = index; });
    this.currentData = {
      ...this.currentData,
      bars,
      dateIndex,
      metadata: {
        ...this.currentData.metadata,
        barCount: bars.length,
        hasBars: bars.length > 0
      }
    };
    this.renderer.setIncrementalData(this.currentData);
    this.clearInspection("Data");
    this.keyboardIndex = Math.max(0, Math.min(this.keyboardIndex, bars.length - 1));
    this.accessibility?.update();
    this.syncToolbarState();
    this.renderToolbar();
    this.emit("data", this.currentData);
    this.emit("toolbar", this.toolbarState);
    return reconciled.result;
  }

  setTheme(theme: Partial<BridgeChartTheme>): void {
    this.theme = { ...this.theme, ...theme };
    this.renderer.setTheme(this.theme);
  }

  resize(): void {
    this.renderer.resize();
    this.syncInspectionViewport();
    this.accessibility?.update();
  }

  fitContent(): void {
    this.renderer.fitContent();
    this.syncInspectionViewport(true);
    this.accessibility?.update();
  }

  resetView(): void {
    this.renderer.resetView();
    this.syncInspectionViewport(true);
    this.accessibility?.update();
  }

  exportVisibleData() {
    return exportVisibleData(this.currentData, this.viewport);
  }

  setExportProvenance(provenance: BridgeChartExportProvenance | null): void {
    const next = provenance === null ? null : validateExportProvenance(provenance, this.currentData.symbol, this.currentData.timeframe, this.drawingPriceBasis);
    this.exportProvenance = next; this.accessibility?.update();
  }

  async exportDataPackage() {
    if (!this.exportProvenance) throw new TypeError("ChartExportProvenanceRequired");
    const csv = this.exportVisibleData();
    return createDataPackage(csv, this.viewport, this.exportProvenance);
  }

  setStudyVisibility(code: BridgeChartStudyCode, visible: boolean): void {
    this.renderer.setStudyVisibility(code, visible);
    this.toolbarStateValue = applyToolbarCommand(this.toolbarStateValue, {
      action: "setIndicatorVisibility",
      code,
      visible
    });
    this.renderToolbar();
    this.emit("toolbar", this.toolbarState);
  }

  setPaneHeight(code: BridgeChartStudyCode, height: number | null): void {
    this.renderer.setPaneHeight(code, height);
  }

  setPresentationState(state: Partial<BridgeChartPresentationState>): void {
    this.presentation = normalizePresentation({ ...this.presentation, ...state }, this.presentation.status);
    this.presentationExplicit = true;
    this.renderPresentation();
  }

  destroy(): void {
    this.disposers.splice(0).forEach(dispose => dispose());
    this.accessibility?.destroy(); this.accessibility = null;
    this.resizeObserver?.disconnect();
    this.container.removeAttribute("data-bridge-chart-ready");
    if (this.tooltip) this.tooltip.hidden = true;
    this.status.hidden = true;
    this.emit("destroy", undefined);
    this.handlers.clear();
  }

  addOverlay(overlay: unknown): void {
    this.overlays.push(overlay);
    this.renderer.draw();
  }

  removeOverlay(id: string): void {
    const index = this.overlays.findIndex(overlay => typeof overlay === "object" && overlay !== null && "id" in overlay && String((overlay as { id?: unknown }).id) === id);
    if (index >= 0) this.overlays.splice(index, 1);
    this.renderer.draw();
  }

  setTimeframe(timeframe: string): void {
    this.requireCurrentTimeframe(timeframe);
    this.toolbarStateValue = applyToolbarCommand(this.toolbarStateValue, {
      action: "setTimeframe",
      value: timeframe as BridgeChartTimeframe
    });
    this.renderToolbar();
    this.emit("toolbar", this.toolbarState);
  }

  private requireCurrentTimeframe(timeframe: string): void {
    const requested = applyToolbarCommand(this.toolbarStateValue, {
      action: "setTimeframe", value: timeframe as BridgeChartTimeframe
    }).timeframe;
    if (requested !== this.toolbarStateValue.timeframe) {
      throw new TypeError("BridgeChartTimeframeDataRequired: use setData with a complete dataset for the requested timeframe.");
    }
  }

  setPeriod(period: BridgeChartPeriod): void {
    this.toolbarStateValue = applyToolbarCommand(this.toolbarStateValue, { action: "setPeriod", value: period });
    this.currentData = { ...this.currentData, metadata: { ...this.currentData.metadata, period } };
    this.applyPeriodViewport();
    this.keyboardIndex = Math.max(0, this.viewport.end);
    this.clearInspection("Viewport");
    this.renderToolbar();
    this.accessibility?.update();
    this.emit("data", this.currentData);
    this.emit("toolbar", this.toolbarState);
  }

  private applyPeriodViewport(): void {
    if (!this.currentData.metadata.period) return;
    const bars = this.currentData.bars;
    const end = bars.length - 1;
    const period = this.toolbarStateValue.period;
    if (end < 0 || period === "ALL") {
      this.renderer.setView(0, end);
      return;
    }
    const months = { "1M": 1, "3M": 3, "6M": 6, "1Y": 12, "2Y": 24 }[period];
    // Anchor to the last trading bar, retaining full input and indicator warmup.
    const cutoff = new Date(`${bars[end].date}T00:00:00Z`);
    const day = cutoff.getUTCDate();
    cutoff.setUTCDate(1);
    cutoff.setUTCMonth(cutoff.getUTCMonth() - months);
    const monthEnd = new Date(cutoff);
    monthEnd.setUTCMonth(monthEnd.getUTCMonth() + 1, 0);
    cutoff.setUTCDate(Math.min(day, monthEnd.getUTCDate()));
    const firstDate = cutoff.toISOString().slice(0, 10);
    // parseChartData/reconcileIncrementalBars keep canonical dates sorted and unique.
    // Locate the first included session without scanning or copying the archive.
    let start = 0, limit = bars.length;
    while (start < limit) {
      const middle = start + Math.floor((limit - start) / 2);
      if (bars[middle].date < firstDate) start = middle + 1;
      else limit = middle;
    }
    this.renderer.setView(start, end);
  }

  setChartType(chartType: BridgeChartType): void {
    this.toolbarStateValue = applyToolbarCommand(this.toolbarStateValue, { action: "setChartType", value: chartType });
    this.renderToolbar();
    this.emit("toolbar", this.toolbarState);
  }

  setMarkerVisibility(visible: boolean): void {
    this.renderer.setMarkerVisibility(visible);
  }

  setSettingsOpen(open: boolean): void {
    this.toolbarStateValue = applyToolbarCommand(this.toolbarStateValue, { action: "setSettingsOpen", value: open });
    this.renderToolbar();
    this.emit("toolbar", this.toolbarState);
  }

  async toggleFullscreen(): Promise<boolean> {
    const ownerDocument = this.container.ownerDocument as Document;
    try {
      if (ownerDocument.fullscreenElement === this.container) {
        if (typeof ownerDocument.exitFullscreen !== "function") return false;
        await ownerDocument.exitFullscreen();
        this.setFullscreenState(false);
        return false;
      }
      if (typeof this.container.requestFullscreen !== "function") return false;
      await this.container.requestFullscreen();
      this.setFullscreenState(true);
      return true;
    } catch {
      this.container.dataset.bridgeChartToolbarError = "FullscreenUnavailable";
      return false;
    }
  }

  setDrawingTool(tool: BridgeChartDrawingKind | null): void {
    this.drawingStore.setTool(tool);
    this.keyboardAnchor = null;
    this.syncDrawings();
  }

  createDrawing(drawing: Omit<BridgeChartDrawing, "id"> & { id?: string }): BridgeChartDrawing {
    const transform = this.renderer.drawingTransform();
    if (!transform) throw new TypeError("Market drawings require nonempty chart data and a price pane.");
    if (drawing.anchors && !transform.matches(drawing.anchors)) throw new TypeError("Drawing market context is mismatched.");
    const created = this.drawingStore.create({ ...drawing, anchors: drawing.anchors ?? transform.anchors(drawing.start, drawing.end) });
    this.syncDrawings();
    return created;
  }

  selectDrawing(id: string | null): void {
    this.drawingStore.select(id);
    this.syncDrawings();
  }

  moveDrawing(id: string, delta: BridgeChartDrawingPoint): void {
    this.drawingStore.move(id, delta);
    this.syncDrawings();
  }

  resizeDrawing(id: string, handle: BridgeChartDrawingHandle, point: BridgeChartDrawingPoint): void {
    this.drawingStore.resize(id, handle, point);
    this.syncDrawings();
  }

  deleteDrawing(id: string): void {
    this.drawingStore.delete(id);
    this.syncDrawings();
  }

  undoDrawing(): boolean {
    const undone = this.drawingStore.undo();
    if (undone) this.syncDrawings();
    return undone;
  }

  clearDrawings(): void {
    this.drawingStore.clear();
    this.syncDrawings();
  }

  hitTestDrawing(point: BridgeChartDrawingPoint, tolerance?: number): BridgeChartDrawingHit | null {
    return this.drawingStore.hitTest(point, tolerance);
  }

  serializeDrawings(): string {
    return this.drawingStore.serialize();
  }

  importDrawings(serialized: string, legacyContext?: BridgeChartDrawingMigrationContext): void {
    this.drawingStore.import(serialized, legacyContext);
    this.syncDrawings();
  }

  setDrawingPriceBasis(priceBasis: string): void {
    this.renderer.setDrawingPriceBasis(priceBasis);
    if (priceBasis !== this.drawingPriceBasis) this.clearInspection("PriceBasis");
    this.drawingPriceBasis = priceBasis; this.exportProvenance = null; this.keyboardAnchor = null; this.drawingGesture = null;
    this.syncDrawings();
  }

  on(eventName: string, handler: BridgeChartEventHandler): () => void {
    if (!this.handlers.has(eventName)) this.handlers.set(eventName, new Set());
    this.handlers.get(eventName)!.add(handler);
    return () => this.handlers.get(eventName)?.delete(handler);
  }

  private attach(): void {
    if ("ResizeObserver" in window) {
      this.resizeObserver = new ResizeObserver(() => this.resize());
      this.resizeObserver.observe(this.canvas.parentElement || this.canvas);
    } else {
      this.listen(window, "resize", () => this.resize());
    }
    this.listen(this.canvas, "mousemove", event => {
      const point = pointFromEvent(this.canvas, event as MouseEvent);
      this.renderer.crosshair = point;
      this.renderer.draw();
      const index = this.renderer.indexFromX(point.x);
      const bar = this.currentData.bars[index];
      this.emit("crosshair", bar ? { date: bar.date, index } : null);
      if (!this.drag && !this.drawingGesture) this.inspectBar(index);
    });
    this.listen(this.canvas, "mouseleave", () => {
      this.renderer.crosshair = null;
      this.drag = null;
      if (this.tooltip) this.tooltip.hidden = true;
      this.renderer.draw();
      this.emit("crosshair", null);
    });
    this.listen(this.canvas, "mousedown", event => {
      const mouseEvent = event as MouseEvent;
      if (this.beginDrawingGesture("mouse", mouseEvent)) {
        mouseEvent.preventDefault();
        this.drag = null;
        return;
      }
      this.drag = { x: pointFromEvent(this.canvas, mouseEvent).x, start: this.renderer.viewStart, end: this.renderer.viewEnd };
    });
    this.listen(window, "mouseup", event => {
      this.finishDrawingGesture("mouse", event as MouseEvent);
      this.drag = null;
    });
    this.listen(window, "mousemove", event => {
      if (this.updateDrawingGesture("mouse", event as MouseEvent)) return;
      if (!this.drag) return;
      const point = pointFromEvent(this.canvas, event as MouseEvent);
      const deltaBars = Math.round((this.drag.x - point.x) / Math.max(1, this.renderer.barWidth()));
      this.renderer.setView(this.drag.start + deltaBars, this.drag.end + deltaBars);
      this.syncInspectionViewport();
      this.accessibility?.update();
    });
    this.listen(this.canvas, "wheel", event => {
      const ev = event as WheelEvent;
      ev.preventDefault();
      const point = pointFromEvent(this.canvas, ev);
      const index = this.renderer.indexFromX(point.x);
      const factor = ev.deltaY < 0 ? 0.82 : 1.18;
      this.renderer.zoomAround(index, factor);
      this.syncInspectionViewport();
      this.accessibility?.update();
    }, { passive: false });
    this.listen(this.canvas, "pointerdown", event => this.onPointerDown(event as PointerEvent));
    this.listen(this.canvas, "pointermove", event => this.onPointerMove(event as PointerEvent));
    this.listen(this.canvas, "pointerup", event => this.onPointerEnd(event as PointerEvent));
    this.listen(this.canvas, "pointercancel", event => this.onPointerCancel(event as PointerEvent));
    this.listen(this.canvas, "lostpointercapture", event => this.onPointerCancel(event as PointerEvent));
    this.listen(this.canvas, "keydown", event => {
      const key = event as KeyboardEvent;
      if (key.altKey || key.metaKey || key.isComposing) return;
      const command = key.ctrlKey ? (key.key.toLowerCase() === "z" ? "undo" : "") : key.shiftKey && key.key.startsWith("Arrow") ? `move:${key.key}` : key.key;
      if (this.keyboardCommand(command)) key.preventDefault();
    });
  }

  private keyboardCommand(key: string): boolean {
    if (key === "Escape") {
      this.keyboardAnchor = null; this.drawingGesture = null; this.drag = null; this.pointerDrag = null; this.pinch = null; this.pointers.clear();
      this.setDrawingTool(null); return true;
    }
    if (!this.currentData.bars.length) return false;
    const count = this.currentData.bars.length, view = this.viewport, span = view.end - view.start + 1;
    const selected = this.drawingStore.state().selectedId;
    let message: string | undefined;
    try {
      if (key.startsWith("move:Arrow") && selected) {
        const direction = key.slice(5); this.moveDrawing(selected, { x: direction === "ArrowLeft" ? -0.01 : direction === "ArrowRight" ? 0.01 : 0, y: direction === "ArrowUp" ? -0.01 : direction === "ArrowDown" ? 0.01 : 0 });
      } else switch (key) {
        case "ArrowLeft": this.keyboardIndex--; break;
        case "ArrowRight": this.keyboardIndex++; break;
        case "Home": this.keyboardIndex = 0; break;
        case "End": this.keyboardIndex = count - 1; break;
        case "PageUp": this.keyboardIndex -= span; break;
        case "PageDown": this.keyboardIndex += span; break;
        case "ArrowUp": this.keyboardPrice = Math.max(0, this.keyboardPrice - 0.01); break;
        case "ArrowDown": this.keyboardPrice = Math.min(1, this.keyboardPrice + 0.01); break;
        case "+": case "=": this.renderer.zoomAround(this.keyboardIndex, 0.82); break;
        case "-": this.renderer.zoomAround(this.keyboardIndex, 1.18); break;
        case "0": this.fitContent(); break;
        case "reset": this.resetView(); this.keyboardIndex = this.viewport.start; break;
        case "Delete": case "Backspace": if (selected) this.deleteDrawing(selected); break;
        case "undo": this.undoDrawing(); break;
        case "[": case "]": {
          const transform = this.renderer.drawingTransform();
          const drawings = this.drawingState.drawings.filter(d => !d.anchors || transform?.matches(d.anchors));
          if (drawings.length) { const current = drawings.findIndex(d => d.id === selected); this.selectDrawing(drawings[(current + (key === "]" ? 1 : drawings.length - 1) + drawings.length) % drawings.length].id); }
          break;
        }
        case "Enter": {
          const tool = this.drawingState.activeTool, transform = this.renderer.drawingTransform();
          if (!tool || !transform) return false;
          const point = { x: (this.keyboardIndex - view.start + 0.5) / span, y: this.keyboardPrice };
          if (!this.keyboardAnchor) {
            this.keyboardAnchor = transform.anchors(point, point); message = this.presentation.locale === "fa" ? "نقطهٔ اول ثبت شد؛ نقطهٔ دوم را انتخاب کنید" : "First anchor set; choose the second anchor";
          } else {
            const anchors = { ...this.keyboardAnchor, end: transform.capture(point) };
            this.createDrawing({ kind: tool, start: point, end: point, anchors }); this.keyboardAnchor = null;
          }
          break;
        }
        default: return false;
      }
      this.keyboardIndex = Math.max(0, Math.min(count - 1, this.keyboardIndex));
      const current = this.viewport;
      if (this.keyboardIndex < current.start) this.renderer.setView(this.keyboardIndex, this.keyboardIndex + current.end - current.start);
      else if (this.keyboardIndex > current.end) this.renderer.setView(this.keyboardIndex - (current.end - current.start), this.keyboardIndex);
      if (key === "ArrowUp" || key === "ArrowDown") {
        const transform = this.renderer.drawingTransform(), visible = this.viewport;
        const point = { x: (this.keyboardIndex - visible.start + 0.5) / (visible.end - visible.start + 1), y: this.keyboardPrice };
        if (transform) message = `${this.presentation.locale === "fa" ? "قیمت ترسیم" : "Drawing price"}: ${transform.capture(point).price}`;
      }
    } catch { message = this.presentation.locale === "fa" ? "عملیات در زمینهٔ فعلی در دسترس نیست" : "Operation unavailable in the current context"; }
    this.syncInspectionViewport();
    if (["ArrowLeft", "ArrowRight", "Home", "End", "PageUp", "PageDown"].includes(key)) this.inspectBar(this.keyboardIndex);
    this.accessibility?.update(this.presentation.locale, message); return true;
  }

  private goToSession(date: string): boolean {
    if (!/^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(date)) return false;
    const index = this.currentData.dateIndex[date];
    // Use the normalized assignment's exact date index, never a nearest session,
    // an inferred calendar date, a scan, or a new data request.
    if (!Number.isInteger(index) || this.currentData.bars[index]?.date !== date) return false;
    this.keyboardCommand("Escape");
    this.keyboardIndex = index;
    const view = this.viewport, span = view.end - view.start;
    if (index < view.start) this.renderer.setView(index, index + span);
    else if (index > view.end) this.renderer.setView(index - span, index);
    this.syncInspectionViewport();
    this.inspectBar(index);
    this.accessibility?.update();
    return true;
  }

  private showRecordedRange(first: string, last: string): "Shown" | "Unavailable" | "Changed" {
    const assignment = this.currentData;
    if (![first, last].every(date => /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(date))) return "Unavailable";
    const start = assignment.dateIndex[first], end = assignment.dateIndex[last];
    // Reject rather than silently taking the tail when the renderer would
    // enforce its existing visible-bar budget. No scan, slicing or acquisition.
    if (!Number.isInteger(start) || !Number.isInteger(end) || start > end ||
        assignment.bars[start]?.date !== first || assignment.bars[end]?.date !== last ||
        end - start + 1 > this.performanceState.budget.maxVisibleBars) return "Unavailable";
    this.keyboardCommand("Escape");
    if (this.currentData !== assignment) return "Changed";
    this.keyboardIndex = end;
    this.renderer.setView(start, end);
    this.syncInspectionViewport();
    // Clearing old evidence invokes host callbacks, which can replace data or
    // navigate elsewhere. Never inspect an old index in that replacement view.
    const matches = () => this.currentData === assignment && this.viewport.start === start && this.viewport.end === end;
    if (!matches()) return "Changed";
    this.inspectBar(end);
    this.accessibility?.update();
    return matches() ? "Shown" : "Changed";
  }

  private inspectBar(index: number): void {
    const view = this.viewport, bar = this.currentData.bars[index];
    if (!bar || index < view.start || index > view.end || this.inspectedIndex === index) return;
    this.inspectedIndex = index;
    this.emit("inspection", { state: "Inspected", date: bar.date, index } satisfies BridgeChartInspection);
  }

  private clearInspection(reason: "Viewport" | "Data" | "PriceBasis"): void {
    this.inspectedIndex = null;
    this.emit("inspection", { state: "Cleared", reason } satisfies BridgeChartInspection);
  }

  private syncInspectionViewport(forceClear = false): void {
    const view = this.viewport, signature = `${view.start}:${view.end}`;
    if (!forceClear && signature === this.inspectionViewport) return;
    this.inspectionViewport = signature;
    this.clearInspection("Viewport");
  }

  private attachToolbar(): void {
    if (!this.toolbar) return;
    this.listen(this.toolbar, "change", event => {
      const target = event.target as HTMLInputElement | HTMLSelectElement | null;
      if (!target?.dataset) return;
      try {
        if (target.dataset.chartIndicator) {
          this.setStudyVisibility(target.dataset.chartIndicator as BridgeChartStudyCode, Boolean((target as HTMLInputElement).checked));
          return;
        }
        switch (target.dataset.chartControl) {
          case "markers": this.setMarkerVisibility(Boolean((target as HTMLInputElement).checked)); break;
          case "drawing": this.setDrawingTool(target.value ? target.value as BridgeChartDrawingKind : null); break;
          case "timeframe": this.setTimeframe(target.value); break;
          case "period": this.setPeriod(target.value as BridgeChartPeriod); break;
          case "chart-type": this.setChartType(target.value as BridgeChartType); break;
        }
      } catch {
        this.container.dataset.bridgeChartToolbarError = "InvalidControlValue";
        this.renderToolbar();
      }
    });
    this.listen(this.toolbar, "click", event => {
      const original = event.target as HTMLElement | null;
      const target = original?.closest?.<HTMLElement>("[data-chart-action]") ?? original;
      const action = target?.dataset?.chartAction;
      if (!action || target?.getAttribute("aria-disabled") === "true") return;
      switch (action) {
        case "fit": this.fitContent(); break;
        case "reset": this.resetView(); break;
        case "fullscreen": void this.toggleFullscreen(); break;
        case "settings": this.setSettingsOpen(!this.toolbarStateValue.settingsOpen); break;
        case "clear-studies": this.studies.forEach(study => this.setStudyVisibility(study.code, false)); break;
        case "remove-study": this.setStudyVisibility(target!.dataset.studyCode as BridgeChartStudyCode, false); break;
        case "undo-drawing": this.undoDrawing(); break;
      }
    });
    this.listen(this.toolbar, "input", event => {
      const target = event.target as HTMLInputElement | null;
      if (!target?.hasAttribute?.("data-chart-study-search")) return;
      const query = target.value.trim().toLocaleLowerCase();
      let matches = 0;
      this.toolbar!.querySelectorAll<HTMLElement>("[data-study-search]").forEach(row => {
        row.hidden = !row.dataset.studySearch!.toLocaleLowerCase().includes(query);
        if (!row.hidden) matches++;
      });
      const empty = this.toolbar!.querySelector<HTMLElement>("[data-chart-study-empty]");
      if (empty) empty.hidden = matches > 0;
    });
    this.listen(this.toolbar, "keydown", event => {
      if ((event as KeyboardEvent).key !== "Escape") return;
      this.toolbar!.querySelectorAll<HTMLDetailsElement>("details[open]").forEach(menu => {
        menu.open = false;
        menu.querySelector<HTMLElement>("summary")?.focus();
      });
      this.setSettingsOpen(false);
    });
    const ownerDocument = this.container.ownerDocument as Document;
    if (typeof ownerDocument.addEventListener === "function") {
      this.listen(ownerDocument, "click", event => {
        this.toolbar!.querySelectorAll<HTMLDetailsElement>("details[open]").forEach(menu => {
          if (!menu.contains(event.target as Node)) menu.open = false;
        });
      });
      this.listen(ownerDocument, "fullscreenchange", () => {
        this.setFullscreenState(ownerDocument.fullscreenElement === this.container);
      });
    }
  }

  private onPointerDown(event: PointerEvent): void {
    if (event.pointerType === "mouse") return;
    event.preventDefault();
    if (this.beginDrawingGesture(event.pointerId, event)) {
      this.canvas.setPointerCapture?.(event.pointerId);
      return;
    }
    const point = pointFromEvent(this.canvas, event);
    this.pointers.set(event.pointerId, point);
    this.canvas.setPointerCapture?.(event.pointerId);
    if (this.pointers.size === 1) {
      this.pointerDrag = {
        pointerId: event.pointerId,
        x: point.x,
        start: this.renderer.viewStart,
        end: this.renderer.viewEnd
      };
      return;
    }
    if (this.pointers.size === 2) {
      const [a, b] = [...this.pointers.values()];
      this.pinch = {
        distance: Math.max(1, Math.hypot(a.x - b.x, a.y - b.y)),
        centerIndex: this.renderer.indexFromX((a.x + b.x) / 2),
        start: this.renderer.viewStart,
        end: this.renderer.viewEnd
      };
      this.pointerDrag = null;
    }
  }

  private onPointerMove(event: PointerEvent): void {
    if (event.pointerType === "mouse") return;
    if (this.updateDrawingGesture(event.pointerId, event)) {
      event.preventDefault();
      return;
    }
    if (!this.pointers.has(event.pointerId)) return;
    event.preventDefault();
    const point = pointFromEvent(this.canvas, event);
    this.pointers.set(event.pointerId, point);
    if (this.pinch && this.pointers.size >= 2) {
      const [a, b] = [...this.pointers.values()];
      const distance = Math.max(1, Math.hypot(a.x - b.x, a.y - b.y));
      this.renderer.setView(this.pinch.start, this.pinch.end);
      this.renderer.zoomAround(this.pinch.centerIndex, this.pinch.distance / distance);
      this.syncInspectionViewport();
      this.accessibility?.update();
      return;
    }
    if (this.pointerDrag?.pointerId === event.pointerId) {
      const deltaBars = Math.round((this.pointerDrag.x - point.x) / Math.max(1, this.renderer.barWidth()));
      this.renderer.setView(this.pointerDrag.start + deltaBars, this.pointerDrag.end + deltaBars);
      this.syncInspectionViewport();
      this.accessibility?.update();
    }
  }

  private onPointerEnd(event: PointerEvent): void {
    if (event.pointerType === "mouse") return;
    if (this.finishDrawingGesture(event.pointerId, event)) {
      if (this.canvas.hasPointerCapture?.(event.pointerId)) this.canvas.releasePointerCapture(event.pointerId);
      return;
    }
    if (this.pointers.has(event.pointerId) && this.pointers.size === 1 && !this.pinch) {
      this.inspectBar(this.renderer.indexFromX(pointFromEvent(this.canvas, event).x));
    }
    this.pointers.delete(event.pointerId);
    if (this.canvas.hasPointerCapture?.(event.pointerId)) this.canvas.releasePointerCapture(event.pointerId);
    this.pinch = null;
    const remaining = [...this.pointers.entries()][0];
    this.pointerDrag = remaining ? {
      pointerId: remaining[0],
      x: remaining[1].x,
      start: this.renderer.viewStart,
      end: this.renderer.viewEnd
    } : null;
  }

  private onPointerCancel(event: PointerEvent): void {
    const ownsGesture = this.drawingGesture?.inputId === event.pointerId || (event.pointerType === "mouse" && this.drawingGesture?.inputId === "mouse");
    if (!ownsGesture && !this.pointers.has(event.pointerId) && !(event.pointerType === "mouse" && this.drag)) return;
    if (ownsGesture) this.drawingGesture = null;
    if (event.pointerType === "mouse") this.drag = null;
    this.pointers.delete(event.pointerId); this.pinch = null;
    const remaining = [...this.pointers.entries()][0];
    this.pointerDrag = remaining ? { pointerId: remaining[0], x: remaining[1].x, start: this.renderer.viewStart, end: this.renderer.viewEnd } : null;
    if (this.canvas.hasPointerCapture?.(event.pointerId)) this.canvas.releasePointerCapture(event.pointerId);
  }

  private beginDrawingGesture(inputId: "mouse" | number, event: MouseEvent): boolean {
    const point = this.renderer.drawingPointFromCanvas(pointFromEvent(this.canvas, event));
    const state = this.drawingStore.state();
    if (state.activeTool) {
      this.drawingGesture = { inputId, origin: point, current: point, tool: state.activeTool, hit: null };
      return true;
    }
    const hit = this.drawingStore.hitTest(point);
    if (!hit) {
      if (state.selectedId !== null) this.selectDrawing(null);
      return false;
    }
    this.drawingStore.select(hit.id);
    this.drawingGesture = { inputId, origin: point, current: point, tool: null, hit };
    this.syncDrawings();
    return true;
  }

  private updateDrawingGesture(inputId: "mouse" | number, event: MouseEvent): boolean {
    if (!this.drawingGesture || this.drawingGesture.inputId !== inputId) return false;
    this.drawingGesture.current = this.renderer.drawingPointFromCanvas(pointFromEvent(this.canvas, event));
    event.preventDefault();
    return true;
  }

  private finishDrawingGesture(inputId: "mouse" | number, event: MouseEvent): boolean {
    const gesture = this.drawingGesture;
    if (!gesture || gesture.inputId !== inputId) return false;
    gesture.current = this.renderer.drawingPointFromCanvas(pointFromEvent(this.canvas, event));
    this.drawingGesture = null;
    if (gesture.tool) {
      this.createDrawing({ kind: gesture.tool, start: gesture.origin, end: gesture.current });
      return true;
    }
    if (!gesture.hit) return true;
    if (gesture.hit.region === "body") {
      this.moveDrawing(gesture.hit.id, {
        x: gesture.current.x - gesture.origin.x,
        y: gesture.current.y - gesture.origin.y
      });
    } else {
      this.resizeDrawing(gesture.hit.id, gesture.hit.region, gesture.current);
    }
    return true;
  }

  private syncDrawings(): void {
    const state = this.drawingStore.state();
    this.renderer.setDrawings(state);
    this.emit("drawings", state);
    this.accessibility?.update();
  }

  private renderPresentation(): void {
    const messages = PRESENTATION_MESSAGES[this.presentation.locale];
    if (this.ownsCanvasLabel) this.canvas.setAttribute?.("aria-label", this.presentation.locale === "fa" ? "نمودار تعاملی؛ کنترل با صفحه‌کلید" : "Interactive chart; keyboard controls");
    this.status.dir = this.presentation.locale === "fa" ? "rtl" : "ltr";
    this.status.lang = this.presentation.locale;
    this.status.hidden = this.presentation.status === "ready";
    this.status.dataset.status = this.presentation.status;
    this.status.textContent = this.presentation.status === "stale"
      ? `${messages.stale} ${this.presentation.staleAsOf}`
      : messages[this.presentation.status];
    this.container.dataset.bridgeChartState = this.presentation.status;
    this.accessibility?.update(this.presentation.locale);
  }

  private syncToolbarState(): void {
    this.toolbarStateValue = createToolbarState(this.currentData, this.renderer.studyStates(), {
      chartType: this.toolbarStateValue.chartType,
      settingsOpen: this.toolbarStateValue.settingsOpen,
      fullscreen: this.toolbarStateValue.fullscreen
    });
  }

  private setFullscreenState(value: boolean): void {
    this.toolbarStateValue = applyToolbarCommand(this.toolbarStateValue, { action: "setFullscreen", value });
    this.renderToolbar();
    this.emit("toolbar", this.toolbarState);
  }

  private renderToolbar(): void {
    if (!this.toolbar) return;
    this.toolbar.dataset.chartSymbol = this.toolbarStateValue.symbol;
    this.toolbar.dataset.chartTimeframe = this.toolbarStateValue.timeframe;
    this.toolbar.dataset.chartPeriod = this.toolbarStateValue.period;
    this.toolbar.dataset.chartType = this.toolbarStateValue.chartType;
    setToolbarValue(this.toolbar, "symbol", this.toolbarStateValue.symbol);
    setToolbarValue(this.toolbar, "timeframe", this.toolbarStateValue.timeframe);
    this.toolbar.querySelectorAll?.('[data-chart-control="timeframe"] option').forEach(element => {
      const option = element as HTMLOptionElement;
      option.disabled = option.value !== this.toolbarStateValue.timeframe;
    });
    setToolbarValue(this.toolbar, "period", this.toolbarStateValue.period);
    setToolbarValue(this.toolbar, "chart-type", this.toolbarStateValue.chartType);
    this.toolbar.querySelectorAll?.("[data-chart-indicator]").forEach(element => {
      const input = element as HTMLInputElement;
      input.checked = this.toolbarStateValue.enabledIndicators.includes(input.dataset.chartIndicator as BridgeChartStudyCode);
    });
    this.toolbar.querySelectorAll?.("[data-chart-action='remove-study']").forEach(element => {
      const chip = element as HTMLElement;
      chip.hidden = !this.toolbarStateValue.enabledIndicators.includes(chip.dataset.studyCode as BridgeChartStudyCode);
    });
    const count = this.toolbar.querySelector?.("[data-chart-study-count]");
    if (count) count.textContent = String(this.toolbarStateValue.enabledIndicators.length);
    const settings = this.toolbar.querySelector?.("[data-bridge-chart-settings]") as HTMLElement | null;
    if (settings) settings.hidden = !this.toolbarStateValue.settingsOpen;
    const settingsButton = this.toolbar.querySelector?.('[data-chart-action="settings"]') as HTMLElement | null;
    settingsButton?.setAttribute("aria-expanded", String(this.toolbarStateValue.settingsOpen));
    const fullscreenButton = this.toolbar.querySelector?.('[data-chart-action="fullscreen"]') as HTMLElement | null;
    fullscreenButton?.setAttribute("aria-pressed", String(this.toolbarStateValue.fullscreen));
  }

  private listen(target: EventTarget, eventName: string, handler: EventListenerOrEventListenerObject, options?: AddEventListenerOptions): void {
    target.addEventListener(eventName, handler, options);
    this.disposers.push(() => target.removeEventListener(eventName, handler, options));
  }

  private emit(eventName: string, payload: unknown): void {
    this.handlers.get(eventName)?.forEach(handler => handler(payload));
  }
}

function findOrCreateCanvas(container: HTMLElement): HTMLCanvasElement {
  const existing = container.querySelector("[data-bridge-chart-canvas]");
  if (existing) return existing as HTMLCanvasElement;
  const canvas = container.ownerDocument.createElement("canvas");
  canvas.className = "bridge-chart-canvas";
  canvas.setAttribute("data-bridge-chart-canvas", "");
  container.appendChild(canvas);
  return canvas;
}

function findOrCreateTooltip(container: HTMLElement): HTMLElement {
  const existing = container.querySelector("[data-bridge-chart-tooltip]");
  if (existing) return existing as HTMLElement;
  const tooltip = container.ownerDocument.createElement("div");
  tooltip.className = "bridge-chart-tooltip";
  tooltip.setAttribute("data-bridge-chart-tooltip", "");
  tooltip.hidden = true;
  container.appendChild(tooltip);
  return tooltip;
}

function findOrCreateStatus(container: HTMLElement): HTMLElement {
  const existing = container.querySelector("[data-bridge-chart-status]");
  const status = existing as HTMLElement | null ?? container.ownerDocument.createElement("div");
  if (!status.classList?.contains("bridge-chart-status")) status.className = "bridge-chart-status";
  status.setAttribute("data-bridge-chart-status", "");
  status.setAttribute("role", "status");
  status.setAttribute("aria-live", "polite");
  if (!existing) (container.querySelector(".bridge-chart-stage") ?? container).appendChild(status);
  return status;
}

function findToolbar(container: HTMLElement): HTMLElement | undefined {
  return container.querySelector("[data-bridge-chart-toolbar]") as HTMLElement | null ?? undefined;
}

function setToolbarValue(toolbar: HTMLElement, name: string, value: string): void {
  const element = toolbar.querySelector?.(`[data-chart-control="${name}"]`) as HTMLInputElement | HTMLOutputElement | HTMLSelectElement | null;
  if (!element) return;
  element.value = value;
  if (element.tagName === "OUTPUT") element.textContent = value;
}

function pointFromEvent(canvas: HTMLCanvasElement, ev: MouseEvent): { x: number; y: number } {
  const rect = canvas.getBoundingClientRect();
  return { x: ev.clientX - rect.left, y: ev.clientY - rect.top };
}

interface DrawingGesture {
  inputId: "mouse" | number;
  origin: BridgeChartDrawingPoint;
  current: BridgeChartDrawingPoint;
  tool: BridgeChartDrawingKind | null;
  hit: BridgeChartDrawingHit | null;
}

const PRESENTATION_MESSAGES = {
  en: { ready: "", loading: "Loading chart data", empty: "No chart data is available", stale: "Chart data is stale as of" },
  fa: { ready: "", loading: "در حال بارگذاری داده‌های نمودار", empty: "داده‌ای برای نمودار موجود نیست", stale: "داده‌های نمودار قدیمی است؛ زمان آخرین داده" }
} as const;

function normalizeInteraction(value?: Partial<BridgeChartInteractionOptions>): BridgeChartInteractionOptions {
  const minVisibleBars = value?.minVisibleBars ?? 18;
  const maxDevicePixelRatio = value?.maxDevicePixelRatio ?? 2;
  if (!Number.isInteger(minVisibleBars) || minVisibleBars < 2 || minVisibleBars > 500) {
    throw new RangeError("BridgeChart minVisibleBars must be an integer from 2 through 500.");
  }
  if (!Number.isFinite(maxDevicePixelRatio) || maxDevicePixelRatio < 1 || maxDevicePixelRatio > 3) {
    throw new RangeError("BridgeChart maxDevicePixelRatio must be from 1 through 3.");
  }
  return { minVisibleBars, maxDevicePixelRatio };
}

function normalizePresentation(
  value: Partial<BridgeChartPresentationState> | undefined,
  fallbackStatus: BridgeChartPresentationState["status"]
): BridgeChartPresentationState {
  const status = value?.status ?? fallbackStatus;
  const locale = value?.locale ?? "en";
  if (!["ready", "loading", "empty", "stale"].includes(status)) {
    throw new TypeError("BridgeChart presentation status is invalid.");
  }
  if (locale !== "en" && locale !== "fa") {
    throw new TypeError("BridgeChart presentation locale must be en or fa.");
  }
  const staleAsOf = value?.staleAsOf;
  if (status === "stale" && (typeof staleAsOf !== "string" || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/.test(staleAsOf))) {
    throw new TypeError("BridgeChart stale presentation requires an exact UTC staleAsOf timestamp.");
  }
  return staleAsOf === undefined ? { status, locale } : { status, locale, staleAsOf };
}
